import { useCallback, useEffect, useState } from "react";
import type { TranslationKey } from "../../lib/i18n";

const backgrounds = [
  { sport: "baseball", src: "/img/baseball-splash.png", label: "guest.landing.sports.baseball" },
  { sport: "football", src: "/img/football-splash.png", label: "guest.landing.sports.football" },
  { sport: "hockey", src: "/img/hockey-splash.png", label: "guest.landing.sports.hockey" },
] as const;

export function resolveAvailableBackground(activeIndex: number, loaded: number[], failed: number[]) {
  return failed.includes(activeIndex) ? loaded[0] ?? activeIndex : activeIndex;
}

export function useSportsBackgroundRotation() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState<number[]>([]);
  const [failed, setFailed] = useState<number[]>([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReducedMotion(preference.matches);
    const updateVisibility = () => setPageVisible(document.visibilityState === "visible");
    updateMotion();
    updateVisibility();
    preference.addEventListener("change", updateMotion);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => {
      preference.removeEventListener("change", updateMotion);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);

  useEffect(() => {
    // Image recovery must also work while autoplay is paused or motion is reduced.
    const availableIndex = resolveAvailableBackground(activeIndex, loaded, failed);
    if (availableIndex !== activeIndex) setActiveIndex(availableIndex);
  }, [activeIndex, loaded, failed]);

  useEffect(() => {
    if (paused || reducedMotion || !pageVisible || loaded.length < 2) return;
    const timer = window.setTimeout(() => {
      // Skip failed or still-loading artwork so rotation never reveals a blank slide.
      for (let offset = 1; offset < backgrounds.length; offset += 1) {
        const next = (activeIndex + offset) % backgrounds.length;
        if (loaded.includes(next)) {
          setActiveIndex(next);
          break;
        }
      }
    }, 9000);
    return () => window.clearTimeout(timer);
  }, [activeIndex, paused, reducedMotion, pageVisible, loaded]);

  const onImageLoad = useCallback((index: number) => {
    setLoaded((previous) => previous.includes(index) ? previous : [...previous, index]);
  }, []);
  const onImageError = useCallback((index: number) => {
    setFailed((previous) => previous.includes(index) ? previous : [...previous, index]);
    setLoaded((previous) => previous.filter((readyIndex) => readyIndex !== index));
  }, []);
  const onSelect = (index: number) => {
    setActiveIndex(index);
    setPaused(true);
  };

  return {
    activeIndex, loaded, onImageLoad, onImageError, onSelect,
    activeSportKey: backgrounds[activeIndex].label,
    availableSportKeys: backgrounds.filter((_, index) => loaded.includes(index)).map((background) => background.label),
    onSelectSport: (sportKey: TranslationKey) => {
      const index = backgrounds.findIndex((background) => background.label === sportKey);
      if (index >= 0 && loaded.includes(index)) onSelect(index);
    },
  };
}

export function SportsBackgroundImages({ activeIndex, onImageLoad, onImageError }: {
  activeIndex: number;
  onImageLoad: (index: number) => void;
  onImageError: (index: number) => void;
}) {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {backgrounds.map((background, index) => (
        <img
          key={background.sport}
          src={background.src}
          alt=""
          width={1024}
          height={1536}
          loading="eager"
          decoding="async"
          fetchPriority={index === 0 ? "high" : "low"}
          onLoad={() => onImageLoad(index)}
          onError={() => onImageError(index)}
          className="absolute inset-0 h-full w-full scale-[1.02] object-cover transition-opacity duration-[2000ms] ease-in-out motion-reduce:transition-none"
          style={{ opacity: activeIndex === index ? 1 : 0 }}
        />
      ))}
    </div>
  );
}

function SportIcon({ sport }: { sport: typeof backgrounds[number]["sport"] }) {
  return (
    <svg aria-hidden="true" className="h-8 w-8" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {sport === "baseball" && <>
        <circle cx="16" cy="16" r="12" />
        <path d="M8 6c8 5 8 15 0 20M24 6c-8 5-8 15 0 20" strokeDasharray="2 3" />
      </>}
      {sport === "football" && <g transform="rotate(-35 16 16)">
        <path d="M2 16c7-13 21-13 28 0-7 13-21 13-28 0Z" />
        <path d="M11 16h10M12 13v6M16 13v6M20 13v6M7 10v12M25 10v12" />
      </g>}
      {sport === "hockey" && <>
        <path d="M24 4 12 23H5v4h9L28 6ZM8 23v4" />
        <ellipse cx="24" cy="25" rx="4" ry="1.5" />
        <path d="M20 25v3c0 2 8 2 8 0v-3" />
      </>}
    </svg>
  );
}

export function SportsBackgroundControls({ activeIndex, onSelect, t, loaded }: {
  activeIndex: number;
  onSelect: (index: number) => void;
  t: (key: TranslationKey) => string;
  loaded?: number[];
}) {
  return (
    <div role="group" aria-label={t("guest.landing.background.label")} className="mt-4 flex items-center gap-3">
      {backgrounds.map((background, index) => (
        <button
          key={background.sport}
          type="button"
          aria-label={t(background.label)} aria-pressed={activeIndex === index}
          title={t(background.label)}
          disabled={loaded !== undefined && !loaded.includes(index)}
          onClick={() => onSelect(index)}
          className={`flex h-11 w-11 items-center justify-center rounded-lg border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#F5A623] disabled:opacity-30 ${activeIndex === index ? "border-[#F5A623]/40 bg-[#F5A623]/10 text-[#F5A623]" : "border-transparent text-white/70 hover:bg-white/5 hover:text-white"}`}
        >
          <SportIcon sport={background.sport} />
        </button>
      ))}
    </div>
  );
}
